import { useState } from "react";

const CourseForm = ({
  course = {},
  instructors = [],
  onSave,
  onCancel,
  readOnly = false,
}) => {
  const [formData, setFormData] = useState({
    name: course.name || "",
    instructor: course.instructor || "",
    startDate: course.startDate || "",
    endDate: course.endDate || "",
    price: course.price || "",
    status: course.status || "active",
    image: null,
  });

  const [previewImage, setPreviewImage] = useState(course.imageUrl || null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prevFormData) => ({ ...prevFormData, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.onerror = (error) => {
        console.error("Error reading file:", error);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSubmit = new FormData();
    for (const key in formData) {
      dataToSubmit.append(key, formData[key]);
    }
    onSave(dataToSubmit);
  };

  const getImageSrc = () => {
    if (!previewImage) return "";
    return previewImage.startsWith("data:")
      ? previewImage
      : previewImage.startsWith("http")
      ? previewImage
      : `http://localhost:5000${previewImage}`;
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-lg p-4 mx-auto space-y-4 overflow-y-auto bg-white rounded-lg shadow-lg"
      style={{ maxHeight: "80vh" }}
    >
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700">
          Course Image
        </label>
        {previewImage ? (
          <div className="mb-2">
            <img
              src={getImageSrc()}
              alt="Course preview"
              className="object-cover w-full h-32 rounded-md"
              onError={(e) => {
                console.error("Image failed to load");
                e.target.style.display = "none";
              }}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center w-full h-32 bg-gray-100 rounded-md">
            <span className="text-gray-400">Image Preview</span>
          </div>
        )}
        {!readOnly && (
          <input
            type="file"
            name="image"
            onChange={handleImageChange}
            accept="image/*"
            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700">
          Course Name
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            readOnly ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
          required
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700">
          Instructor
        </label>
        <select
          name="instructor"
          value={formData.instructor}
          onChange={handleChange}
          className={`w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            readOnly ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
          required
          disabled={readOnly}
        >
          <option value="">-- Select Instructor --</option>
          {instructors.map((inst) => (
            <option key={inst._id} value={inst._id}>
              {inst.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700">
          Start Date
        </label>
        <input
          type="date"
          name="startDate"
          value={formData.startDate}
          onChange={handleChange}
          className={`w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            readOnly ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
          required
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700">
          End Date
        </label>
        <input
          type="date"
          name="endDate"
          value={formData.endDate}
          onChange={handleChange}
          className={`w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            readOnly ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
          required
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700">
          Price
        </label>
        <input
          type="number"
          name="price"
          value={formData.price}
          onChange={handleChange}
          className={`w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            readOnly ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
          required
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-700">
          Status
        </label>
        <select
          name="status"
          value={formData.status}
          onChange={handleChange}
          className={`w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            readOnly ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
          required
          disabled={readOnly}
        >
          <option value="active">Active</option>
          <option value="upcoming">Upcoming</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      <div className="flex space-x-4">
        {!readOnly && (
          <button
            type="submit"
            className="px-6 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Save Course"
          >
            Save
          </button>
        )}
        <button
          type="button"
          className="px-6 py-2 text-gray-700 bg-gray-300 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
          onClick={onCancel}
          aria-label="Cancel"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default CourseForm;
